'use client';

import React, {useCallback, useMemo} from 'react';
import Image from 'next/image';

import {
  AuthenticationController,
  TabType,
  AWSManager,
  CountriesList,
  SMSVerifyController,
  SMSPhoneInput,
  _m,
  Trans,
} from '@sdk-mobile';
import RegistrationStep from '@/app/(app)/games/[gid]/sign-in/_src/RegistrationStep';

const AWS_EVENTS = {
  EXIT_PRESS: 'SIGN_IN_EXIT',
  INFO_PRESS: 'SIGN_IN_INFO',
  VIEW_PHONE_TAB: 'SIGN_IN_PHONE_VIEW',
  VIEW_EMAIL_TAB: 'SIGN_IN_EMAIL_VIEW',
  AREA_CODE_BOX_PRESS: 'SIGN_IN_PHONE_AREACODE_TAP',
  SEND_CODE: 'SIGN_IN_PHONE_SEND',
  SEND_CODE_CALL: 'SIGN_IN_PHONE_CALL_SEND',
  ENTER_CODE: 'SIGN_IN_SMS_ENTER',
};

const SKILL_TOS_URL = 'https://skillz.com/legal';

type Props = {
  country: string;
  countryValue: string;
  errorStyle: boolean;
  errorMessage?: string;
  gameIcon?: string;
  gameTitle?: string;
  onSendPressed: () => void;
  phoneNumber: string;
  setCountry: (country: string) => void;
  setPhoneNumber: (phone: string) => void;
  setShowCountryList: (visible: boolean) => void;
  showCountryList: boolean;
  liveEvent?: boolean;
};

export default function PhoneInput({
  country,
  countryValue,
  errorStyle,
  errorMessage,
  gameIcon,
  gameTitle,
  onSendPressed,
  phoneNumber,
  setCountry,
  setPhoneNumber,
  setShowCountryList,
  showCountryList,
  liveEvent,
}: Props) {
  const onSignIn = useCallback(
    () =>
      AuthenticationController.displaySignInScreen(
        false,
        undefined,
        undefined,
        undefined,
        'dark',
        TabType.EMAIL
      ),
    []
  );
  const countryData = useMemo(() => SMSVerifyController.getCountryData(), []);

  return (
    <>
      {liveEvent ? <RegistrationStep step={1} /> : null}
      <div
        className={
          'w-[90%] max-w-[500px] relative text-white flex flex-col justify-center items-center bg-white bg-opacity-5 border border-white backdrop-blur-md shadow-lg rounded-lg mx-20 py-4 shadow-[0_4px_15px_rgba(255,255,255,0.2)]'
        }
      >
        <p className={'text-white text-center font-bold -mb-2.75'}>
          {_m('Enter your phone number')}
        </p>
        <div className={'px-4 py-4 w-full'}>
          <SMSPhoneInput
            useSmallSize
            colorVariant='dark'
            errorStyle={errorStyle}
            errorMessage={errorMessage}
            getFlag={SMSVerifyController.getFlag}
            showCountryList={{
              value: showCountryList,
              set: setShowCountryList,
            }}
            messageText={{}}
            country={country}
            countryValue={countryValue}
            trackEvent={AWSManager.trackEvent}
            phoneNumber={{value: phoneNumber, set: setPhoneNumber}}
            formatNumber={SMSVerifyController.formatNumber}
            events={{
              countrySelect: AWS_EVENTS.AREA_CODE_BOX_PRESS,
              codeRequest: AWS_EVENTS.SEND_CODE,
              callRequest: AWS_EVENTS.SEND_CODE_CALL,
              codeEnter: AWS_EVENTS.ENTER_CODE,
            }}
          />
        </div>
        <div className={'w-full relative text-white flex flex-col justify-center items-center'}>
          <p className={'text-white text-center text-xs'}>
            {_m('Already have an account?') + ' '}
            <button className={'text-blue-500 font-bold inline text-s'} onClick={onSignIn}>
              {_m('Sign in')}
            </button>
          </p>
        </div>
      </div>
      {gameIcon ? (
        <div
          className={
            'w-screen grow relative text-white flex flex-col justify-center items-center px-6 mt-6'
          }
        >
          <Image
            src={gameIcon}
            className='rounded-md'
            alt={gameTitle || 'Skillz'}
            width={90}
            height={90}
            priority={true}
          />
        </div>
      ) : null}
      <div
        className={
          'w-screen grow relative text-white flex flex-col justify-center items-center px-6'
        }
      >
        <button
          className={
            'w-full max-w-[400px] bg-[#308ee2] p-3 mb-4 mt-2 rounded-xl text-white font-bold text-lg'
          }
          onClick={onSendPressed}
        >
          {_m('Next')}
        </button>
        <p className={'text-white text-xs font-light mb-2'}>
          <Trans
            t={_m}
            i18nKey={'SMS_TOS_COPY'}
            components={{
              tos: <a href={SKILL_TOS_URL} target='_blank' className={'text-blue-500'} />,
            }}
          />
        </p>
      </div>

      <CountriesList
        visible={showCountryList}
        hide={() => {
          setShowCountryList(false);
        }}
        select={(value: string) => setCountry(value)}
        data={countryData}
        getFlag={SMSVerifyController.getFlag}
      />
    </>
  );
}
